import React, { useEffect, useMemo, useState } from 'react';
import { FormDrawer } from './FormDrawer';
import { emptyUpsellingLine, findUpsellingProductOption } from '../placeholders/upsellingCatalogUtils';

const STATUS_OPTIONS = [
  { value: 'Borrador', label: 'Borrador' },
  { value: 'Enviada', label: 'Enviada' },
  { value: 'Aprobada', label: 'Aprobada' },
  { value: 'Rechazada', label: 'Rechazada' },
  { value: 'Convertida a OC', label: 'Convertida a OC' },
];

const PRIORITY_OPTIONS = [
  { value: 'Baja', label: 'Baja' },
  { value: 'Normal', label: 'Normal' },
  { value: 'Alta', label: 'Alta' },
  { value: 'Urgente', label: 'Urgente' },
];

const PURCHASE_MOTIVE_OPTIONS = [
  { value: 'stock', label: 'Stock' },
  { value: 'sale', label: 'Venta' },
];

const emptyLine = () => ({
  id: null,
  item_code: '',
  description: '',
  quantity: 1,
  unit: 'UN',
  estimated_unit_price: 0,
});

const unitPriceForItem = (item, useProductCatalog) => {
  if (useProductCatalog) {
    const budget = Number(item?.unit_budget);
    if (Number.isFinite(budget) && budget > 0) return budget;
  }
  return Number(item?.estimated_unit_price || 0);
};

const lineTotal = (item, useProductCatalog = false) => {
  const qty = Number(item?.quantity || 0);
  const price = unitPriceForItem(item, useProductCatalog);
  return Math.round(qty * price * 100) / 100;
};

const formatMoney = (value) =>
  new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const productLabelForItem = (item, productOptions = []) => {
  const code = String(item?.item_code || '').trim();
  if (!code) return '';
  const option = findUpsellingProductOption(productOptions, code);
  if (option?.name) return option.name;
  return String(item?.description || code).trim() || code;
};

const filterProductOptions = (options = [], query = '') => {
  const tokens = String(query || '')
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
  const list = (options || []).filter((option) => option.value);
  if (!tokens.length) return list.slice(0, 20);
  return list
    .filter((option) => {
      const hay = [option.name, option.label, option.value].filter(Boolean).join(' ').toLowerCase();
      return tokens.every((token) => hay.includes(token));
    })
    .slice(0, 20);
};

const buildSelectOptions = (options = [], placeholder, currentValue = '') => {
  const value = String(currentValue || '').trim();
  const hasOption = !value || options.some((opt) => opt.value === value);
  return [
    { value: '', label: placeholder },
    ...options,
    ...(value && !hasOption ? [{ value, label: value }] : []),
  ];
};

export const PurchaseRequestsForm = ({
  isOpen,
  onClose,
  onSave,
  isSaving = false,
  isDarkMode = false,
  isEditing = false,
  formData,
  onChange,
  formError = '',
  supplierOptions = [],
  areaOptions = [],
  requesterOptions = null,
  buyerOptions = null,
  saleOptions = [],
  productOptions = null,
  formSubtitle = 'Adquisiciones',
}) => {
  const items = Array.isArray(formData?.items) ? formData.items : [];
  const [productSearch, setProductSearch] = useState({});
  const [openProductIndex, setOpenProductIndex] = useState(null);

  const useProductCatalog = Array.isArray(productOptions) && productOptions.length > 0;
  const effectiveBuyerOptions = buyerOptions ?? requesterOptions;

  const totals = useMemo(() => {
    const subtotal = items.reduce((sum, item) => sum + lineTotal(item, useProductCatalog), 0);
    return { subtotal, total: subtotal };
  }, [items, useProductCatalog]);

  const updateItem = (index, key, value) => {
    const next = items.map((item, idx) => (idx === index ? { ...item, [key]: value } : item));
    onChange('items', next);
  };

  const updateItemFields = (index, patch) => {
    const next = items.map((item, idx) => (idx === index ? { ...item, ...patch } : item));
    onChange('items', next);
  };

  const addItem = () =>
    onChange('items', [...items, useProductCatalog ? emptyUpsellingLine() : emptyLine()]);
  const removeItem = (index) => onChange('items', items.filter((_, idx) => idx !== index));

  const productSelectOptionsForItem = (item) => {
    const code = String(item?.item_code || '').trim();
    const options = [...(productOptions || [])];
    if (code && !options.some((option) => option.value === code)) {
      options.push({
        value: code,
        name: String(item?.description || code).trim() || code,
        label: String(item?.description || code).trim() || code,
      });
    }
    return options;
  };

  const applyProductSelection = (index, productCode) => {
    const option = findUpsellingProductOption(productOptions, productCode);
    const next = items.map((row, idx) => {
      if (idx !== index) return row;
      if (!productCode) {
        return { ...row, item_code: '', description: '' };
      }
      if (!option) {
        return { ...row, item_code: productCode };
      }
      const price = option.unit_price ?? 0;
      return {
        ...row,
        item_code: option.value,
        description: option.name || option.label,
        estimated_unit_price: price,
        unit_budget: price,
        unit: option.unit || row.unit || 'UN',
      };
    });
    onChange('items', next);
  };

  useEffect(() => {
    if (!isOpen) {
      setProductSearch({});
      setOpenProductIndex(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!useProductCatalog || !isOpen) return;
    setProductSearch((prev) => {
      const next = { ...prev };
      items.forEach((item, index) => {
        if (openProductIndex === index) return;
        const label = productLabelForItem(item, productOptions);
        if (label) next[index] = label;
      });
      return next;
    });
  }, [items, isOpen, useProductCatalog, productOptions, openProductIndex]);

  const departmentValue = formData?.department || '';
  const hasDepartmentOption = areaOptions.some((opt) => opt.value === departmentValue);
  const requesterValue = String(formData?.requester_name || '').trim();
  const hasRequesterOption =
    !requesterOptions?.length || requesterOptions.some((opt) => opt.value === requesterValue);
  const requesterSelectOptions = requesterOptions?.length
    ? [
        { value: '', label: 'Seleccionar solicitante' },
        ...requesterOptions,
        ...(requesterValue && !hasRequesterOption ? [{ value: requesterValue, label: requesterValue }] : []),
      ]
    : null;

  const sections = [
    {
      key: 'basic',
      title: 'Datos de la solicitud',
      fields: [
        { key: 'request_code', label: 'Código', disabled: true, readOnly: true },
        requesterSelectOptions
          ? {
              key: 'requester_name',
              label: 'Solicitante',
              type: 'select',
              options: requesterSelectOptions,
            }
          : { key: 'requester_name', label: 'Solicitante' },
        {
          key: 'department',
          label: 'Área / departamento',
          type: 'select',
          options: [
            { value: '', label: 'Seleccionar área' },
            ...areaOptions,
            ...(departmentValue && !hasDepartmentOption
              ? [{ value: departmentValue, label: departmentValue }]
              : []),
          ],
        },
        {
          key: 'supplier_id',
          label: 'Proveedor sugerido',
          type: 'select',
          options: [{ value: '', label: 'Sin proveedor' }, ...supplierOptions],
        },
        {
          key: 'priority',
          label: 'Prioridad',
          type: 'select',
          options: PRIORITY_OPTIONS,
        },
        {
          key: 'status',
          label: 'Estado',
          type: 'select',
          options: STATUS_OPTIONS,
        },
        { key: 'request_date', label: 'Fecha solicitud', type: 'date' },
        { key: 'needed_by_date', label: 'Necesario para', type: 'date' },
        { key: 'justification', label: 'Justificación', type: 'textarea', fullWidth: true, rows: 3 },
        { key: 'notes', label: 'Notas', type: 'textarea', fullWidth: true, rows: 2 },
      ],
    },
    {
      key: 'items',
      title: 'Ítems solicitados',
      fields: [
        {
          key: 'items',
          fullWidth: true,
          type: 'custom',
          render: ({ colors, baseInputStyle }) => (
            <div style={{ display: 'grid', gap: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: colors.muted }}>
                  Estimado total: {formatMoney(totals.total)}
                </span>
                <button
                  type="button"
                  onClick={addItem}
                  style={{
                    height: 30,
                    padding: '0 10px',
                    borderRadius: 8,
                    border: `1px solid ${colors.inputBorder}`,
                    background: 'transparent',
                    color: colors.text,
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  Agregar ítem
                </button>
              </div>
              {items.length === 0 ? (
                <p style={{ margin: 0, fontSize: 12, color: colors.muted }}>Sin ítems. Agrega al menos uno.</p>
              ) : (
                items.map((item, index) => {
                  const motive = String(item.purchase_motive || 'stock').trim() || 'stock';
                  const showSaleSelect = useProductCatalog && motive === 'sale';
                  const buyerSelectOptions = buildSelectOptions(
                    effectiveBuyerOptions || [],
                    'Seleccionar comprador',
                    item.buyer_name
                  );
                  const saleSelectOptions = buildSelectOptions(
                    saleOptions,
                    'Seleccionar venta',
                    item.sale_id ? String(item.sale_id) : ''
                  );

                  return (
                    <div
                      key={item.id || `new-${index}`}
                      style={{
                        display: 'grid',
                        gap: 8,
                        padding: 10,
                        borderRadius: 8,
                        border: `1px solid ${colors.sectionBorder}`,
                        background: colors.sectionBg,
                      }}
                    >
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: useProductCatalog
                            ? '1.6fr 0.8fr 0.7fr 1.4fr 1fr'
                            : '1fr 2fr 0.7fr 0.7fr 0.9fr auto',
                          gap: 8,
                          alignItems: 'end',
                        }}
                      >
                        {useProductCatalog ? (
                          <div style={{ position: 'relative' }}>
                            <label style={{ fontSize: 11, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                              Producto
                              <input
                                style={{ ...baseInputStyle, marginTop: 4 }}
                                value={productSearch[index] ?? productLabelForItem(item, productOptions)}
                                placeholder="Busca por nombre…"
                                onFocus={() => setOpenProductIndex(index)}
                                onBlur={() =>
                                  setTimeout(() => setOpenProductIndex((prev) => (prev === index ? null : prev)), 120)
                                }
                                onChange={(event) => {
                                  const typedValue = event.target.value;
                                  setProductSearch((prev) => ({ ...prev, [index]: typedValue }));
                                  setOpenProductIndex(index);
                                }}
                              />
                            </label>
                            {openProductIndex === index ? (
                              <div
                                style={{
                                  position: 'absolute',
                                  top: '58px',
                                  left: 0,
                                  right: 0,
                                  maxHeight: 180,
                                  overflowY: 'auto',
                                  background: colors.panel,
                                  border: `1px solid ${colors.inputBorder}`,
                                  borderRadius: 8,
                                  zIndex: 12,
                                  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
                                }}
                              >
                                {filterProductOptions(
                                  productSelectOptionsForItem(item),
                                  productSearch[index] ?? productLabelForItem(item, productOptions)
                                ).map((option) => (
                                  <button
                                    key={`${index}-${option.value}`}
                                    type="button"
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={() => {
                                      setProductSearch((prev) => ({ ...prev, [index]: option.name || option.label }));
                                      applyProductSelection(index, option.value);
                                      setOpenProductIndex(null);
                                    }}
                                    style={{
                                      width: '100%',
                                      textAlign: 'left',
                                      padding: '8px 10px',
                                      border: 'none',
                                      background: 'transparent',
                                      color: colors.text,
                                      cursor: 'pointer',
                                      fontSize: 13,
                                    }}
                                  >
                                    {option.name || option.label}
                                  </button>
                                ))}
                                {filterProductOptions(
                                  productSelectOptionsForItem(item),
                                  productSearch[index] ?? productLabelForItem(item, productOptions)
                                ).length === 0 ? (
                                  <div style={{ padding: '10px 12px', fontSize: 12, color: colors.muted }}>
                                    Sin coincidencias en el catálogo.
                                  </div>
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                        <label style={{ fontSize: 11, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                          Código
                          <input
                            style={{
                              ...baseInputStyle,
                              marginTop: 4,
                              ...(useProductCatalog ? { backgroundColor: colors.sectionBg } : {}),
                            }}
                            value={item.item_code || ''}
                            onChange={(e) => updateItem(index, 'item_code', e.target.value)}
                            readOnly={useProductCatalog}
                          />
                        </label>
                        {!useProductCatalog ? (
                          <label style={{ fontSize: 11, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                            Descripción
                            <input
                              style={{ ...baseInputStyle, marginTop: 4 }}
                              value={item.description || ''}
                              onChange={(e) => updateItem(index, 'description', e.target.value)}
                            />
                          </label>
                        ) : (
                          <label style={{ fontSize: 11, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                            Motivo
                            <select
                              style={{ ...baseInputStyle, marginTop: 4 }}
                              value={motive}
                              onChange={(e) => {
                                const nextMotive = e.target.value;
                                updateItemFields(index, {
                                  purchase_motive: nextMotive,
                                  sale_id: nextMotive === 'sale' ? item.sale_id || '' : '',
                                });
                              }}
                            >
                              {PURCHASE_MOTIVE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </label>
                        )}
                        {useProductCatalog && showSaleSelect ? (
                          <label style={{ fontSize: 11, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                            Venta
                            <select
                              style={{ ...baseInputStyle, marginTop: 4 }}
                              value={item.sale_id ? String(item.sale_id) : ''}
                              onChange={(e) => updateItem(index, 'sale_id', e.target.value)}
                            >
                              {saleSelectOptions.map((option) => (
                                <option key={`${index}-sale-${option.value || 'empty'}`} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </label>
                        ) : useProductCatalog ? (
                          <div />
                        ) : null}
                        {useProductCatalog ? (
                          <label style={{ fontSize: 11, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                            Comprador
                            <select
                              style={{ ...baseInputStyle, marginTop: 4 }}
                              value={item.buyer_name || ''}
                              onChange={(e) => updateItem(index, 'buyer_name', e.target.value)}
                            >
                              {buyerSelectOptions.map((option) => (
                                <option key={`${index}-buyer-${option.value || 'empty'}`} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </label>
                        ) : null}
                        {!useProductCatalog ? (
                          <>
                            <label style={{ fontSize: 11, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                              Cant.
                              <input
                                type="number"
                                step="0.01"
                                style={{ ...baseInputStyle, marginTop: 4 }}
                                value={item.quantity ?? 1}
                                onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                              />
                            </label>
                            <label style={{ fontSize: 11, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                              Unidad
                              <input
                                style={{ ...baseInputStyle, marginTop: 4 }}
                                value={item.unit || 'UN'}
                                onChange={(e) => updateItem(index, 'unit', e.target.value)}
                              />
                            </label>
                            <label style={{ fontSize: 11, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                              Precio est.
                              <input
                                type="number"
                                step="0.01"
                                style={{ ...baseInputStyle, marginTop: 4 }}
                                value={item.estimated_unit_price ?? 0}
                                onChange={(e) => updateItem(index, 'estimated_unit_price', e.target.value)}
                              />
                            </label>
                          </>
                        ) : null}
                        {!useProductCatalog ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                            <span style={{ fontSize: 11, color: colors.muted }}>
                              {formatMoney(lineTotal(item, useProductCatalog))}
                            </span>
                            <button
                              type="button"
                              onClick={() => removeItem(index)}
                              style={{
                                height: 34,
                                padding: '0 10px',
                                borderRadius: 8,
                                border: `1px solid ${colors.inputBorder}`,
                                background: 'transparent',
                                color: '#b91c1c',
                                cursor: 'pointer',
                                fontSize: 12,
                              }}
                            >
                              Quitar
                            </button>
                          </div>
                        ) : null}
                      </div>
                      {useProductCatalog ? (
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '0.7fr 0.7fr 1fr 1.6fr auto',
                            gap: 8,
                            alignItems: 'end',
                          }}
                        >
                          <label style={{ fontSize: 11, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                            Cant.
                            <input
                              type="number"
                              step="0.01"
                              style={{ ...baseInputStyle, marginTop: 4 }}
                              value={item.quantity ?? 1}
                              onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                            />
                          </label>
                          <label style={{ fontSize: 11, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                            Unidad
                            <input
                              style={{ ...baseInputStyle, marginTop: 4 }}
                              value={item.unit || 'UN'}
                              onChange={(e) => updateItem(index, 'unit', e.target.value)}
                            />
                          </label>
                          <label style={{ fontSize: 11, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                            Presupuesto unitario
                            <input
                              type="number"
                              step="0.01"
                              style={{ ...baseInputStyle, marginTop: 4 }}
                              value={item.unit_budget ?? 0}
                              onChange={(e) => updateItem(index, 'unit_budget', e.target.value)}
                            />
                          </label>
                          <label style={{ fontSize: 11, color: colors.text, display: 'flex', flexDirection: 'column' }}>
                            Enlace referencia
                            <input
                              style={{ ...baseInputStyle, marginTop: 4 }}
                              value={item.reference_link || ''}
                              placeholder="https://…"
                              onChange={(e) => updateItem(index, 'reference_link', e.target.value)}
                            />
                          </label>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                            <span style={{ fontSize: 11, color: colors.muted }}>
                              {formatMoney(lineTotal(item, useProductCatalog))}
                            </span>
                            <button
                              type="button"
                              onClick={() => removeItem(index)}
                              style={{
                                height: 34,
                                padding: '0 10px',
                                borderRadius: 8,
                                border: `1px solid ${colors.inputBorder}`,
                                background: 'transparent',
                                color: '#b91c1c',
                                cursor: 'pointer',
                                fontSize: 12,
                              }}
                            >
                              Quitar
                            </button>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  );
                })
              )}
            </div>
          ),
        },
      ],
    },
  ];

  return (
    <FormDrawer
      isOpen={isOpen}
      onClose={onClose}
      onSave={onSave}
      isSaving={isSaving}
      isDarkMode={isDarkMode}
      title={isEditing ? 'Editar solicitud' : 'Nueva solicitud'}
      subtitle={formSubtitle}
      sections={sections}
      formData={formData}
      onChange={onChange}
      formError={formError}
      width={useProductCatalog ? 1040 : 760}
    />
  );
};

export default PurchaseRequestsForm;
